import type { ExerciseKind } from "@edu/shared";

/**
 * Как назвать вид задания человеку.
 *
 * ⚠️ Заведено затем, чтобы наружу не уезжало наше слово. До этого модуля на трёх
 * экранах стояло «Задание „blanks“ появится позже» и «задание „match“» в списке
 * формы раздачи домашки: `element.kind` печатался как есть. Читает эти экраны
 * преподаватель, и `blanks` для него не значит ничего.
 *
 * Отдельным файлом без единого импорта значением: читателей четыре и они в
 * разных чанках (слой упражнений, лента домашки, экран результатов, форма
 * раздачи), а словарь на четыре строки в каждом из них однажды разъехался бы.
 *
 * Названия те же, что на кнопках палитры, и это правило, а не совпадение:
 * человек ставит задание кнопкой «Пропуски» и обязан узнать его под тем же
 * именем везде, где о нём заходит речь.
 */
const LABELS: Record<ExerciseKind, string> = {
  text: "Текст",
  graph: "График функции",
  choice: "Выбор варианта",
  blanks: "Пропуски",
  match: "Соответствие",
  order: "Порядок слов",
  input: "Свободный ввод",
  number: "Число",
  expr: "Выражение",
  derivation: "Запись решения",
  column: "Столбик",
  translate: "Перевод предложений",
};

/** Название вида с большой буквы: годится в начало фразы. */
export function exerciseKindLabel(kind: ExerciseKind): string {
  return LABELS[kind];
}

/**
 * То же название строчными: годится в середину фразы («задание „пропуски“»).
 *
 * ⚠️ Считается из первого словаря, а не набирается вторым списком: два списка
 * одних и тех же одиннадцати слов однажды разойдутся, и разойдутся молча.
 */
export function exerciseKindLabelLower(kind: ExerciseKind): string {
  const label = LABELS[kind];
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/**
 * Вид, которого экран ещё не умеет: «Задание «пропуски» появится позже».
 *
 * ⚠️ **Фраза собирается ЗДЕСЬ целиком, а не шаблоном на месте, и это не
 * причёсывание.** Три экрана писали `{название} появится позже`, то есть ставили
 * название подлежащим, а «Пропуски» это единственная метка во множественном
 * числе: с ней выходило «Пропуски появится позже». Сегодня недостижимо (пропуски
 * умеют все три экрана), но ловушка была заложена и сработала бы на первом же
 * виде, который назовут словом во множественном числе, вроде «Соответствия».
 *
 * Подлежащее теперь всегда «задание», а название стоит в кавычках приложением,
 * поэтому число метки на согласование не влияет вовсе. Замок в
 * `kind-label.test.ts`: он проверяет форму фразы у ВСЕХ одиннадцати видов, то есть
 * ловит и новый вид, дописанный в словарь.
 */
export function exerciseKindTodoText(kind: ExerciseKind): string {
  return `Задание «${exerciseKindLabelLower(kind)}» появится позже`;
}
